<template>
  <WithBackdrop
    :backdrop="data?.fields?.backdrop"
    :class="[$style.config, $style.wrap]"
  >
    <div :class="$style.inner">
      <header :class="$style.header">
        <BaseHeading size="h4" as="h2" :class="$style.heading">
          {{ data?.fields?.header }}
        </BaseHeading>
      </header>
      <div ref="swiperRef" :class="['swiper', $style.cards]">
        <div class="swiper-wrapper">
          <div
            v-for="card in computedCards"
            :key="card?.sys?.id"
            class="swiper-slide"
          >
            <component
              :is="
                computedCtas?.[card?.sys?.id]?.link?.startsWith('http')
                  ? 'a'
                  : NuxtLinkLocale
              "
              :to="
                computedCtas?.[card?.sys?.id]?.link?.startsWith('http')
                  ? undefined
                  : computedCtas?.[card?.sys?.id]?.link
              "
              :href="computedCtas?.[card?.sys?.id]?.link"
              :target="computedCtas?.[card?.sys?.id]?.target"
            >
              <div :class="$style.card">
                <AspectRatio ratio="297x338">
                  <BasePicture
                    :src="`${card?.fields?.media?.fields?.file?.url}?fit=thumb&q=90&w=297`"
                    alt=""
                  />
                </AspectRatio>
                <div :class="$style.cardInfoWrap">
                  <div :class="$style.cardInfo">
                    <BaseHeading size="h6" as="h3">
                      {{ computedLabels?.[card?.sys?.id]?.sliced }}
                      <span :class="$style.trailing">
                        {{ computedLabels?.[card?.sys?.id]?.last }}&nbsp;
                        <IconCardArrowRight :class="$style.icon" />
                      </span>
                    </BaseHeading>
                  </div>
                </div>
              </div>
            </component>
          </div>
        </div>
      </div>
    </div>
  </WithBackdrop>
</template>

<script setup lang="ts">
import type { Swiper } from 'swiper'
import swiper from 'swiper'
import { NuxtLinkLocale } from '#components'

import type { TypeContentBlockWithoutUnresolvableLinksResponse } from '~/types/contentful'

import 'swiper/css'

const props = defineProps<{
  data: TypeContentBlockWithoutUnresolvableLinksResponse
}>()

const swiperRef = ref<(HTMLDivElement & { swiper: Swiper }) | null>(null)

const computedCards = computed(() =>
  props.data?.fields?.references?.filter?.(instanceOfCard),
)

const computedCtas = computed(() => {
  const map: { [key: string]: { link: string; target: string } } = {}

  const cards = props.data?.fields?.references?.filter?.(instanceOfCard) || []
  for (const card of cards) {
    const cta = card?.fields?.ctas?.[0]

    map[card?.sys?.id] = {
      link: cta?.fields?.link || '',
      target: cta?.fields?.target || '_blank',
    }
  }

  return map
})

// Get last words of CTA labels to keep the arrow SVG on the same line as the last word
const computedLabels = computed(() => {
  const map: { [key: string]: { sliced: string; last: string } } = {}

  const cards = props.data?.fields?.references?.filter?.(instanceOfCard) || []
  for (const card of cards) {
    const cta = card?.fields?.ctas?.[0]
    const label = cta?.fields?.label || ''
    const split = label.split(' ')

    map[card?.sys?.id] = {
      sliced: split.slice(0, split.length - 1).join(' '),
      last: split.at(-1) || '',
    }
  }

  return map
})

onMounted(() => {
  if (swiperRef.value) {
    new swiper(swiperRef.value, {
      slidesPerView: 1.25,
      slidesPerGroup: 1,
      spaceBetween: 15,
      breakpoints: {
        576: {
          slidesPerView: 2.5,
        },
        768: {
          slidesPerView: 3.5,
        },
        1200: {
          slidesPerView: 5,
          spaceBetween: 20,
        },
      },
    })
  }
})
</script>

<style lang="scss" module>
.config {
  @include breakpoint-variables(
    '--ub-wrap-padding',
    (
      base: 45px 0,
      md: 60px 0,
      lg: 60px 20px,
    )
  );

  @include breakpoint-variables(
    '--ub-card-info-padding',
    (
      base: 15px 20px,
      md: 20px 30px,
    )
  );
}

.wrap {
  padding: var(--ub-wrap-padding);
  padding-left: 20px;
}

.inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 15px;
  max-width: 1600px;
  margin: 0 auto;

  @include media('md') {
    gap: 20px;
  }
}

.header {
  text-align: center;
  width: 100%;
}

.cards {
  width: 100%;
}

.card {
  position: relative;
  overflow: hidden;

  &:after {
    content: '';
    position: absolute;
    right: -24px;
    bottom: -24px;
    width: 48px;
    height: 48px;
    background: var(--purple);
    transform: rotate(45deg);

    @include media('xs') {
      right: -28px;
      bottom: -28px;
      width: 56px;
      height: 56px;
    }

    @include media('md') {
      right: -35px;
      bottom: -35px;
      width: 70px;
      height: 70px;
    }
  }
}

.cardInfoWrap {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;

  display: flex;
  padding: var(--ub-card-info-padding);
  padding-right: 50px;
  background: linear-gradient(to top, rgba(0, 0, 0, 1), rgba(0, 0, 0, 0) 80%);
}

.cardInfo {
  display: flex;
  align-items: flex-end;
  gap: 10px;
  color: var(--white);
}

.trailing {
  white-space: nowrap;
}

.icon {
  display: inline;
  height: 12px;
  width: 16px;

  @include media('md') {
    height: 16px;
    width: 21px;
  }
}
</style>
